import { SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig } from "../types.js";

type BatteryClusterArgs = {
    friendlyName: string;
    friendlyShortName: string;
};

function makeBatteryCluster({
    friendlyName,
    friendlyShortName,
}: BatteryClusterArgs): SensorClusterConfig<Record<string, never>> {
    return {
        communicationMode: SensorPortCommunicationMode.DIGITAL,
        requiredSubChannels: [],
        optionalSubChannels: [],
        isPermanentlyAttached: true,
        parameterConfigs: {},
        manufacturerName: "Verdi",
        modelName: "Battery Voltage",
        friendlyName,
        friendlyShortName,
        generateSensorElements: () => [
            {
                sensorElementConfigKey: "digitalVoltage",
                parameters: {},
                // Device-specific battery chemistry/ranges vary; keep the shared diagnostic
                // element range broad and validate/interpret per firmware context.
                legalConvertedRange: [0, null],
                subChannel: 0,
            },
        ],
    };
}

export const batteryClusterConfigs = {
    Gen4_Battery: makeBatteryCluster({
        friendlyName: "Gen4 Battery",
        friendlyShortName: "Battery",
    }),

    Sprout_Battery: makeBatteryCluster({
        friendlyName: "Sprout Battery",
        friendlyShortName: "Battery",
    }),

    SeedSap_Battery: makeBatteryCluster({
        friendlyName: "Seed/Sapling Battery",
        friendlyShortName: "Battery",
    }),

    Sensoterra_Battery: makeBatteryCluster({
        friendlyName: "Sensoterra Battery",
        friendlyShortName: "Battery",
    }),

    // The station's own console/gateway battery (WLL health telemetry) — distinct from the ISS
    // transmitter's own battery below; a station can report either independently of the other.
    Davis_WeatherLink_Battery: makeBatteryCluster({
        friendlyName: "Davis WeatherLink Console Battery",
        friendlyShortName: "Console Battery",
    }),

    Davis_WeatherLink_TransmitterBattery: makeBatteryCluster({
        friendlyName: "Davis WeatherLink Transmitter Battery",
        friendlyShortName: "Transmitter Battery",
    }),

    DraginoSDI12_Battery: makeBatteryCluster({
        friendlyName: "Dragino SDI-12 Node Battery",
        friendlyShortName: "Battery",
    }),

    EPlantTreeTag_Battery: makeBatteryCluster({
        friendlyName: "ePlant TreeTag Battery",
        friendlyShortName: "Battery",
    }),
} satisfies Record<string, SensorClusterConfig<Record<string, never>>>;
